import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { WorkspaceResponse } from '../../api';
import { BrandMark } from '../BrandMark';
import { Icon } from '../icons';
import type { IconName } from '../icons';
import { Tooltip } from '../primitives/Tooltip';
import { ProfileMenu, ThemeToggle } from '../ui';
import { useMediaQuery } from '../../hooks';

/**
 * AppShell (CO-150): the organization's navigation as a persistent sidebar.
 *
 * - 1025 px and wider: the sidebar is open beside the page; it can be
 *   folded into an icon rail, and that choice is remembered.
 * - 768–1024 px: the icon rail, so the page keeps its width. Every icon has
 *   an accessible name and a tooltip on hover and focus; "Expand
 *   navigation" opens the full sidebar over the page.
 * - Below 768 px: no sidebar. A top bar carries a labelled Menu button that
 *   opens the same navigation as a modal drawer (focus moves in and is kept
 *   there, Escape and the close button close it, focus returns).
 *
 * Organization destinations live here only. A project's own sections are
 * the project's navigation (ProjectNav), so the two never mix.
 */

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

const primary: NavItem[] = [
  { to: '/app', label: 'Dashboard', icon: 'dashboard', end: true },
  { to: '/app/projects', label: 'Projects', icon: 'projects' },
  { to: '/app/servers', label: 'Servers', icon: 'server' },
  { to: '/app/audit-log', label: 'Activity', icon: 'activity' },
];

const secondary: NavItem[] = [
  { to: '/app/team', label: 'Team', icon: 'users' },
  { to: '/app/settings', label: 'Settings', icon: 'settings' },
];

const COLLAPSED_KEY = 'co.sidebar.collapsed';
const SIDEBAR_TOGGLE_ID = 'sidebar-toggle';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // Storage can be unavailable: the choice then lasts for this page.
  }
}

export function AppShell({
  workspace,
  onSignOut,
  children,
}: {
  workspace: WorkspaceResponse;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const phone = useMediaQuery('(max-width: 767.98px)');
  const tablet = useMediaQuery('(min-width: 768px) and (max-width: 1024.98px)');
  const [collapsedPreference, setCollapsedPreference] = useState(readCollapsed);
  // On a tablet the rail is the default; "Expand" opens the sidebar over the page.
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // A navigation closes whatever was opened to make it.
  useEffect(() => {
    setOverlayOpen(false);
    setDrawerOpen(false);
  }, [location.pathname]);

  // The tablet's overlay is not a dialog, but it covers the page like one:
  // Escape closes it, and focus goes back to the button that opened it when it
  // was in the navigation (CO-184, DS12). A dialog or menu that is open takes
  // its own Escape first.
  useEffect(() => {
    if (!(tablet && overlayOpen)) return undefined;
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('dialog[open]')) return;
      const active = document.activeElement;
      const inNavigation = !active || active === document.body || Boolean(document.querySelector('.sidebar')?.contains(active));
      event.preventDefault();
      setOverlayOpen(false);
      if (inNavigation) document.getElementById(SIDEBAR_TOGGLE_ID)?.focus();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [tablet, overlayOpen]);

  const rail = !phone && (tablet ? !overlayOpen : collapsedPreference);
  const orgLabel = workspace.identity.kind === 'session' ? workspace.organization.name : 'Local development';

  const toggleRail = () => {
    if (tablet) {
      setOverlayOpen((value) => !value);
      return;
    }
    setCollapsedPreference((value) => {
      writeCollapsed(!value);
      return !value;
    });
  };

  return (
    <div className="app-shell" data-nav={phone ? 'drawer' : rail ? 'rail' : tablet ? 'overlay' : 'sidebar'}>
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          // Move focus without touching the URL: some sections read the hash.
          event.preventDefault();
          document.getElementById('main-content')?.focus();
        }}
      >
        Skip to content
      </a>

      {phone ? (
        <header className="topbar">
          <MenuButton open={drawerOpen} onOpen={() => setDrawerOpen(true)} />
          <NavLink to="/app" end className="topbar__brand">
            <BrandMark />
            <span>Commerce Ops</span>
          </NavLink>
        </header>
      ) : (
        <aside className={`sidebar${rail ? ' sidebar--rail' : ''}`} aria-label="Commerce Ops">
          <SidebarContent
            rail={rail}
            orgLabel={orgLabel}
            workspace={workspace}
            onSignOut={onSignOut}
            toggle={{ expanded: !rail, onToggle: toggleRail }}
          />
        </aside>
      )}
      {tablet && overlayOpen ? <div className="sidebar-scrim" aria-hidden="true" onClick={() => setOverlayOpen(false)} /> : null}

      {phone ? (
        <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <SidebarContent rail={false} orgLabel={orgLabel} workspace={workspace} onSignOut={onSignOut} />
        </NavDrawer>
      ) : null}

      <div className="app">{children}</div>
    </div>
  );
}

function MenuButton({ open, onOpen }: { open: boolean; onOpen: () => void }) {
  return (
    <button type="button" className="topbar__menu" aria-expanded={open} aria-controls="app-nav-drawer" onClick={onOpen}>
      <Icon name="menu" size={20} />
      <span>Menu</span>
    </button>
  );
}

function SidebarContent({
  rail,
  orgLabel,
  workspace,
  onSignOut,
  toggle,
}: {
  rail: boolean;
  orgLabel: string;
  workspace: WorkspaceResponse;
  onSignOut: () => void;
  toggle?: { expanded: boolean; onToggle: () => void };
}) {
  return (
    <div className="sidebar__inner">
      <div className="sidebar__brand-row">
        <NavLink to="/app" end className="sidebar__brand" aria-label={rail ? 'Commerce Ops dashboard' : undefined}>
          <BrandMark />
          {rail ? null : <span>Commerce Ops</span>}
        </NavLink>
        {toggle ? (
          <Tooltip content={toggle.expanded ? 'Collapse navigation' : 'Expand navigation'} side={rail ? 'right' : 'bottom'}>
            <button
              type="button"
              id={SIDEBAR_TOGGLE_ID}
              className="sidebar__toggle"
              aria-expanded={toggle.expanded}
              aria-label={toggle.expanded ? 'Collapse navigation' : 'Expand navigation'}
              onClick={toggle.onToggle}
            >
              <Icon name="panel-left" size={16} />
            </button>
          </Tooltip>
        ) : null}
      </div>
      {rail ? null : (
        <p className="sidebar__org" title={orgLabel}>
          {orgLabel}
        </p>
      )}
      <nav className="sidebar__nav" aria-label="Main navigation">
        <NavList items={primary} rail={rail} />
      </nav>
      <nav className="sidebar__nav sidebar__nav--secondary" aria-label="Organization">
        <NavList items={secondary} rail={rail} />
      </nav>
      <div className="sidebar__footer">
        {rail ? null : <ThemeToggle className="sidebar__theme" />}
        <ProfileMenu identity={workspace.identity} organization={workspace.organization} onSignOut={onSignOut} placement="up" compact={rail} />
      </div>
    </div>
  );
}

function NavList({ items, rail }: { items: NavItem[]; rail: boolean }) {
  return (
    <ul className="sidebar__list">
      {items.map((item) => {
        const link = (
          <NavLink to={item.to} end={item.end} className="sidebar__link" aria-label={rail ? item.label : undefined}>
            <Icon name={item.icon} size={18} />
            {rail ? null : <span>{item.label}</span>}
          </NavLink>
        );
        return (
          <li key={item.to}>
            {rail ? (
              <Tooltip content={item.label} side="right">
                {link}
              </Tooltip>
            ) : (
              link
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * NavDrawer: the navigation on a phone, a native modal <dialog> from the
 * left edge, so the browser makes the page inert and Escape cancels; focus
 * goes to the close button and returns to the Menu button.
 */
function NavDrawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
      closeRef.current?.focus();
      return () => {
        if (dialog.open) dialog.close();
        if (opener?.isConnected) opener.focus();
      };
    }
    if (!open && dialog.open) dialog.close();
    return undefined;
  }, [open]);

  return (
    <dialog
      ref={ref}
      id="app-nav-drawer"
      className="nav-drawer"
      aria-label="Navigation"
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
      onKeyDown={(event) => {
        // Some contexts deliver Escape without the dialog's cancel step.
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        }
      }}
      onClick={(event) => {
        if (event.target === ref.current) close();
      }}
    >
      {open ? (
        <div className="nav-drawer__panel">
          <button ref={closeRef} type="button" className="nav-drawer__close" onClick={close} aria-label="Close navigation">
            <Icon name="close" size={20} />
          </button>
          {children}
        </div>
      ) : null}
    </dialog>
  );
}
